/**
 * Shared entry-ordering logic for the Client Hub. Used by both the real client-facing page
 * (useJourney.js) and the team's "Preview Journey" overlay (JourneyPreviewOverlay.jsx) so the
 * preview always matches what the client actually sees — previously the preview passed entries
 * straight through in raw authoring (position) order while the live page re-sorted by phase, so a
 * milestone added after the others (like a late-added "Existing Building 3D Model" phase) showed
 * last in the preview even though it correctly showed first on the real page.
 */

// Default phase for the onboarding questionnaire (it isn't milestone-anchored). Matches the
// project's pre-design phase string so it groups with that phase's milestone entries.
export const ONBOARDING_DEFAULT_PHASE = 'Pre-design & Planning Phase'

// Proposals predate every real Phase record (Phases are only created by scaffold-project.js after
// a proposal executes), so they can never resolve to a real phase — and not every project even has
// a 'Pre-design & Planning Phase' to fall back to (e.g. DC-first templates). Give them their own
// bucket instead of lumping them into the generic 'Other' catch-all, and sort it first (below) so
// the signed agreement is the first thing a client/team member sees, regardless of phase template.
// Reference letters ride in the same bucket: they are part of the run-up to signing, and the server
// drops them from the Hub once a proposal is signed (api/_shareDocs.js#buildJourneyPayload).
export const PROPOSAL_BUCKET = 'Proposal'

// Resolved phase for an entry: the milestone's phase (authoritative), else a manual override
// (data.phase, set in authoring), else a sensible default for known phase-anchored types.
export function entryPhase(e) {
  return (e?.milestone?.phase) || (e?.data?.phase)
    || (e?.type === 'onboarding-questionnaire' ? ONBOARDING_DEFAULT_PHASE : '')
    || (e?.type === 'proposal' || e?.type === 'reference' ? PROPOSAL_BUCKET : '') || 'Other'
}

// Resolved ISO date for an entry: a date the team set by hand on a milestone card (data.hubDate),
// else the milestone's post date, else the entry's own date (data.entryDate). Empty when none.
export function entryDateISO(e) { return (e?.data?.hubDate) || (e?.milestone?.postedDate) || (e?.data?.entryDate) || '' }

// Final display order: by the project's phase order, then date ascending within each phase, then the
// original (team-curated) order as a stable tiebreak. Undated entries sort last within their phase.
export function orderEntries(entries, phaseOrder) {
  const phaseIdx = p => { if (p === PROPOSAL_BUCKET) return -1; const i = (phaseOrder || []).indexOf(p); return i === -1 ? 9999 : i }
  return (entries || [])
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      const pa = phaseIdx(entryPhase(a.e)), pb = phaseIdx(entryPhase(b.e))
      if (pa !== pb) return pa - pb
      const da = entryDateISO(a.e), db = entryDateISO(b.e)
      if (da && db && da !== db) return da < db ? -1 : 1
      if (da && !db) return -1
      if (!da && db) return 1
      return a.i - b.i
    })
    .map(x => x.e)
}
